/**
 * Communication templates (board #233 -- channel-aware templates).
 *
 * The page was written against an API that does not exist. Every template row
 * it rendered read `template.channel` (a uuid), `template.channel_name`,
 * `template.body`, `template.is_active` and `template.variables` -- the server
 * returns `channel_type` ('email'), `content`, a six-state `status` and, until
 * now, no `variables` on the list serializer at all. So `template.variables.length`
 * threw on `undefined` and took the whole page down the moment a single
 * template existed. Nobody hit it because production has zero templates, and
 * production has zero templates because the create modal POSTed
 * `{name, code, channel: <uuid>, subject, body}`, which the create serializer
 * rejects field by field: a guaranteed 400.
 *
 * The rewrite talks to the real serializer, and the create form now changes
 * shape with the channel: the field list comes from
 * `GET /communications/templates/schema/`, the same table the server validates
 * against, so an email template asks for a subject and a WhatsApp template asks
 * for its registered template name.
 */

import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Badge, Button, Card, EmptyState, Input, Modal, ModalFooter, Pagination, Select } from '@/components/common';
import { PageHeader } from '@/components/layout';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type {
  CommunicationTemplateFilters,
  CommunicationTemplateInput,
  CommunicationTemplateRow,
  TemplateChannelSchema,
} from '@/services/communications';
import {
  useCommunicationTemplates,
  useCreateCommunicationTemplate,
  useGateways,
  useTemplateSchema,
} from './useCommunicationGateways';

const CATEGORY_OPTIONS = [
  { value: 'transactional', label: 'Transactional' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'alert', label: 'Alert' },
  { value: 'notification', label: 'Notification' },
  { value: 'otp', label: 'OTP' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'onboarding', label: 'Onboarding' },
];

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  pending_approval: 'warning',
  approved: 'info',
  rejected: 'error',
  active: 'success',
  inactive: 'default',
};

/** Fields the create form renders as a multi-line box rather than one line. */
const LONG_FIELDS = new Set(['content', 'html_content', 'header_content']);

type FormValues = Record<string, string>;

export function TemplatesPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<CommunicationTemplateFilters>({});
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading } = useCommunicationTemplates(page, filters);
  const { data: schema } = useTemplateSchema();
  const { data: gateways } = useGateways();

  const templates = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // The channel filter lists channels, not gateway rows. The old dropdown
  // listed CommunicationChannel rows by id and sent `?channel=<uuid>`, which
  // the ViewSet does not filter on -- so it never narrowed anything, and with
  // zero gateways configured it was always empty anyway.
  const channelOptions = useMemo(
    () => (schema ?? []).map((row) => ({ value: row.channel_type, label: row.channel_type_display })),
    [schema]
  );

  useEffect(() => {
    const next = debouncedTerm || undefined;
    setFilters((prev) => (prev.search === next ? prev : { ...prev, search: next }));
    setPage(1);
  }, [debouncedTerm]);

  const configuredChannels = new Set((gateways ?? []).filter((g) => g.is_active).map((g) => g.channel_type));

  return (
    <div>
      <PageHeader
        title="Templates"
        subtitle={`${totalItems} communication template${totalItems === 1 ? '' : 's'}`}
        breadcrumbs={[{ label: 'Communications', href: '/communications' }, { label: 'Templates' }]}
        actions={
          <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={() => setShowCreateModal(true)}>
            Create Template
          </Button>
        }
      />

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search templates..."
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
              aria-label="Search templates"
            />
          </div>
          <Select
            aria-label="Filter by channel"
            options={[{ value: '', label: 'All Channels' }, ...channelOptions]}
            value={filters.channel_type || ''}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, channel_type: e.target.value || undefined }));
              setPage(1);
            }}
            className="w-48"
          />
          <Select
            aria-label="Filter by status"
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'approved', label: 'Approved' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={filters.status || ''}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, status: e.target.value || undefined }));
              setPage(1);
            }}
            className="w-48"
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-4" />
              <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded" />
            </Card>
          ))
        ) : templates.length === 0 ? (
          <div className="col-span-full">
            <EmptyState
              title="No templates found"
              description="Create a template for the channel you want to send on."
            />
          </div>
        ) : (
          templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              hasGateway={configuredChannels.has(template.channel_type)}
            />
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setPage}
          />
        </div>
      )}

      <CreateTemplateModal
        isOpen={showCreateModal}
        schema={schema}
        onClose={() => setShowCreateModal(false)}
      />
    </div>
  );
}

function TemplateCard({
  template,
  hasGateway,
}: {
  template: CommunicationTemplateRow;
  hasGateway: boolean;
}) {
  // `variables` is served now, but defaulting is what stops one missing field
  // from taking the page down again.
  const variables = template.variables ?? [];

  return (
    <Card>
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{template.name}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{template.code}</p>
        </div>
        <Badge variant={STATUS_VARIANT[template.status] ?? 'default'} size="sm">
          {template.status_display}
        </Badge>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {/* Which channel this template belongs to -- the thing #233 asked the
            list to show. It is a channel type, not a link to a gateway row:
            templates are keyed on 'email' / 'whatsapp', not on a gateway id. */}
        <Badge variant="info" size="sm">
          {template.channel_type_display}
        </Badge>
        <Badge variant="default" size="sm">
          {template.category_display}
        </Badge>
        {template.channel_type === 'whatsapp' && template.whatsapp_template_name && (
          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{template.whatsapp_template_name}</span>
        )}
      </div>

      {template.subject && <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 truncate">{template.subject}</p>}

      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-3 whitespace-pre-wrap">{template.content_preview}</p>
      </div>

      {variables.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {variables.map((variable) => (
            <span key={variable} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
              {`{{${variable}}}`}
            </span>
          ))}
        </div>
      )}

      {template.channel_type === 'sms' && template.status === 'active' && !template.dlt_template_id && (
        <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">No DLT template ID — Indian aggregators will reject this.</p>
      )}

      {!hasGateway && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          No {template.channel_type_display} gateway is configured, so this template cannot be sent yet.
        </p>
      )}
    </Card>
  );
}

function CreateTemplateModal({
  isOpen,
  schema,
  onClose,
}: {
  isOpen: boolean;
  schema?: TemplateChannelSchema[];
  onClose: () => void;
}) {
  const [channelType, setChannelType] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [category, setCategory] = useState('transactional');
  const [values, setValues] = useState<FormValues>({});
  const createMutation = useCreateCommunicationTemplate();

  useEffect(() => {
    if (!isOpen) return;
    setChannelType('');
    setName('');
    setCode('');
    setCategory('transactional');
    setValues({});
  }, [isOpen]);

  const channelSchema = schema?.find((row) => row.channel_type === channelType);
  const fields = channelSchema
    ? [...channelSchema.required_fields, ...channelSchema.optional_fields]
    : [];

  const missing = (channelSchema?.required_fields ?? []).filter((field) => !values[field]?.trim());
  const canCreate = !!channelType && !!name.trim() && !!code.trim() && missing.length === 0;

  const handleCreate = () => {
    if (!canCreate || !channelSchema) return;

    const payload: CommunicationTemplateInput = {
      name: name.trim(),
      code: code.trim().toUpperCase(),
      category,
      channel_type: channelType,
    };
    // Only fields this channel supports are sent. The server rejects the rest
    // outright, and sending them would be storing data the channel cannot use.
    fields.forEach((field) => {
      const value = values[field]?.trim();
      if (value) (payload as Record<string, string>)[field] = value;
    });

    createMutation.mutate(payload, { onSuccess: onClose });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Template" size="lg">
      <div className="space-y-4">
        <Select
          label="Channel"
          name="template-channel-type"
          placeholder="Select a channel"
          options={(schema ?? []).map((row) => ({
            value: row.channel_type,
            label: row.channel_type_display,
          }))}
          value={channelType}
          onChange={(e) => {
            setChannelType(e.target.value);
            setValues({});
          }}
          required
        />

        {!channelType && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Pick a channel first — each one needs different fields.
          </p>
        )}

        {channelType && (
          <>
            <Input
              label="Name"
              name="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Renewal reminder"
              required
            />
            <Input
              label="Code"
              name="template-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="RENEWAL_REMINDER"
              helperText="Used to look the template up when sending. Unique per channel."
              required
            />
            <Select
              label="Category"
              name="template-category"
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            />

            {fields.map((field) => {
              const label = channelSchema?.field_labels?.[field] ?? field;
              const isRequired = channelSchema?.required_fields.includes(field) ?? false;
              if (LONG_FIELDS.has(field)) {
                return (
                  <div key={field}>
                    <label
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      htmlFor={`template-${field}`}
                    >
                      {label}
                      {isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                    </label>
                    <textarea
                      id={`template-${field}`}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows={field === 'content' ? 4 : 3}
                      value={values[field] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [field]: e.target.value }))}
                      placeholder={
                        field === 'content'
                          ? 'Use {{variable}} for dynamic content'
                          : undefined
                      }
                    />
                  </div>
                );
              }
              return (
                <Input
                  key={field}
                  label={label}
                  name={`template-${field}`}
                  value={values[field] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [field]: e.target.value }))}
                  required={isRequired}
                  helperText={
                    field === 'whatsapp_template_name'
                      ? 'Must already be approved in your WhatsApp provider’s console.'
                      : field === 'dlt_template_id'
                        ? 'Required before an SMS template can be activated in India.'
                        : undefined
                  }
                />
              );
            })}
          </>
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleCreate} isLoading={createMutation.isPending} disabled={!canCreate}>
          Create
        </Button>
      </ModalFooter>
    </Modal>
  );
}
